import { Download, RotateCcw, RotateCw, ZoomIn, ZoomOut } from 'lucide-react'
import { PDF_PREVIEW_MAX_ZOOM as MAX_ZOOM, PDF_PREVIEW_MIN_ZOOM as MIN_ZOOM, PDF_PREVIEW_ZOOM_STEP as ZOOM_STEP } from './pdfPreviewUtils'

const ICON_BUTTON =
    'inline-flex h-8 w-8 items-center justify-center rounded-full text-foreground-muted transition-colors hover:bg-default hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50'

interface PdfPreviewToolbarProps {
    zoom: number
    onZoomChange: (zoom: number) => void
    /** Reset to 100%; owners that track a zoom origin reset it here too. */
    onZoomReset: () => void
    /** Hidden for previews that cannot zoom (e.g. a non-PDF iframe fallback). */
    showZoom?: boolean
    /** Quarter-turn rotate buttons, shown only when provided. Called with -90 (left) or 90 (right). */
    onRotate?: (delta: 90 | -90) => void
    /** The Download button is shown only when provided. */
    onDownload?: () => void
}

const Divider = () => <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />

/** Floating zoom + download pill shared by the PDF viewer, the ZIP viewer and the document lightbox (which adds rotate). */
export const PdfPreviewToolbar = ({ zoom, onZoomChange, onZoomReset, showZoom = true, onRotate, onDownload }: PdfPreviewToolbarProps) => (
    <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 rounded-3xl bg-surface p-1 shadow-field">
        {showZoom && (
            <>
                <button
                    type="button"
                    onClick={() => onZoomChange(Math.max(MIN_ZOOM, zoom - ZOOM_STEP))}
                    disabled={zoom <= MIN_ZOOM}
                    className={ICON_BUTTON}
                    aria-label="Zoom out"
                    title="Zoom out"
                >
                    <ZoomOut className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={onZoomReset}
                    className="min-w-14 rounded-full px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-default"
                    aria-label="Reset zoom"
                    title="Reset zoom"
                >
                    {Math.round(zoom * 100)}%
                </button>
                <button
                    type="button"
                    onClick={() => onZoomChange(Math.min(MAX_ZOOM, zoom + ZOOM_STEP))}
                    disabled={zoom >= MAX_ZOOM}
                    className={ICON_BUTTON}
                    aria-label="Zoom in"
                    title="Zoom in"
                >
                    <ZoomIn className="h-4 w-4" />
                </button>
                {(onRotate || onDownload) && <Divider />}
            </>
        )}
        {onRotate && (
            <>
                <button type="button" onClick={() => onRotate(-90)} className={ICON_BUTTON} aria-label="Rotate left" title="Rotate left">
                    <RotateCcw className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => onRotate(90)} className={ICON_BUTTON} aria-label="Rotate right" title="Rotate right">
                    <RotateCw className="h-4 w-4" />
                </button>
                {onDownload && <Divider />}
            </>
        )}
        {onDownload && (
            <button
                type="button"
                onClick={onDownload}
                className="inline-flex h-8 items-center gap-1.5 rounded-3xl bg-default px-3 text-xs font-medium text-default-foreground transition-colors hover:bg-default-hover"
            >
                <Download className="h-4 w-4" />
                Download
            </button>
        )}
    </div>
)
